// Shortlist Detail Page
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { Box, Button } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import toast from "react-hot-toast";
import BrowsePros from "./BrowsePros";
import { PageTitle } from "../../../components/PageTitle";
import { ReactComponent as AddIcon } from "../../../assets/svg/add-icon.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/svg/download-icon.svg";
import { ReactComponent as GearIcon } from "../../../assets/svg/gear-icon.svg";
import { ReactComponent as BinIcon } from "../../../assets/svg/bin.svg";
import axiosInstant from "../../../api";
import AddShortlistModal from "../../admin/Shortlists/AddShortlistModal";
import ShortlistFormModal from "../../admin/Shortlists/ShortlistFormModal";
import DeleteShortlistModal from "../../admin/Shortlists/DeleteShortListModal";
import { IconShare, IconPencil } from "../../../utils/icons";
import { MainContainer } from "../../../components/ContentWrapper";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import { DotLottleLoader } from "../../../components/Loader/DotLottleLoader";
import AddProjectToShortlist from "../../admin/Shortlists/AddProjectToShortlist";
import { ReactComponent as VideoProductionIcon } from "../../../assets/svg/production.svg";
import { ReactComponent as DuplicateIcon } from "../../../assets/svg/duplicate.svg";
import { DropdownMenu } from "../../../components/Dropdown/Dropdown";
import { MenuIcon } from "../../../components/MenuOptions/MenuIcon";

export default function ShortlistProlisting() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [shortlist, setShortlist] = useState<any>();
  const [shortlistName, setShortlistName] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const [isClearingShortlist, setIsClearingShortlist] = useState(false);
  const [isFetchingShortlist, setIsFetchingShortlist] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [duplicateNameModalOpen, setDuplicateNameModalOpen] = useState(false);

  const { id } = useParams(); // Get id from URL params

  const fetchShortlistById = async () => {
    if (!id) return;
    setIsFetchingShortlist(true);
    try {
      const response = await axiosInstant.get(`/shortlists/${id}`);
      setShortlistName(response?.data?.name);
      setShortlist(response.data);
    } catch (error) {
      console.error("Error fetching shortlist:", error);
      setShortlistName("Shortlists");
    } finally {
      setIsFetchingShortlist(false);
    }
  };

  useEffect(() => {
    fetchShortlistById();
  }, [id]);

  const refreshSearchParams = async () => {
    if (!id) return;
    setSearchParams(new URLSearchParams({ shortlist: id }));
    fetchShortlistById();
  };

  const clearShortlist = async () => {
    if (!shortlist) {
      toast.error("No shortlist.");
      return;
    }

    if (!shortlist || !shortlist.experts.length) {
      toast("This shortlist is already empty.");
      return;
    }

    try {
      setIsClearingShortlist(true);
      // await Promise.all(
      //   shortlist.experts.map((expert: any) =>
      //     axiosInstant.delete(
      //       `/shortlists/${shortlist.id}/experts/${expert.userid}`
      //     )
      //   )
      // );
      await axiosInstant.delete(`/shortlists/${shortlist.id}/clear`);
      toast.success("Shortlist cleared successfully.");
      refreshSearchParams();
    } catch (error) {
      console.error("Error clearing shortlist:", error);
      toast.error("Failed to clear the shortlist. Try again.");
    } finally {
      setIsClearingShortlist(false);
    }
    setShowClearConfirmModal(false);
  };

  const share = () => {
    if (!shortlist?.id) return;
    const url = `/experts/share?shortlistId=${shortlist?.id}`;
    window.open(url, "_blank");
  };

  const updateShortlistName = (newName: string) => {
    setShortlistName(newName);
  };

  if (!id || !shortlist?.id) return null;

  return (
    <MainContainer>
      <Box className="flex flex-col gap-6">
        {isFetchingShortlist ? (
          <DotLottleLoader />
        ) : (
          <div className="mastcontent mb-[100px]">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-4 flex-wrap">
              {shortlistName && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", minWidth: 0 }}
                >
                  <PageTitle title={shortlistName} />
                  <Box
                    sx={{
                      display: "flex",
                      padding: "2px 16px",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50px",
                      background: "var(--tint)",
                      color: "var(--accent-text)",
                      fontFamily: '"CircularStd"',
                      fontSize: "14px",
                      fontStyle: "normal",
                      fontWeight: 900,
                      lineHeight: "normal",
                      marginTop: "3px",
                    }}
                  >
                    Shortlist
                  </Box>
                </Box>
              )}

              <div className="flex items-center gap-2 w-full md:w-auto">
                <Button
                  variant="contained"
                  sx={{ textTransform: "none", flex: { xs: 1, md: "0 0 auto" } }}
                  onClick={() => setIsModalOpen(true)}
                >
                  <AddIcon className="mr-1" />
                  Add expert
                </Button>

                {Boolean(shortlist?.experts.length) && (
                  <Button
                    variant="outlined"
                    sx={{ textTransform: "none", flex: { xs: 1, md: "0 0 auto" } }}
                    onClick={() => setShowClearConfirmModal(true)}
                  >
                    Clear shortlist
                  </Button>
                )}

                <DropdownMenu
                  trigger={<MenuIcon id="export-menu" isDetailedPage />}
                  options={[
                    {
                      label: "Export CSV",
                      onClick: () => setShowDialog(true),
                      icon: DownloadIcon,
                    },
                    {
                      label: "Share",
                      onClick: () => share(),
                      icon: IconShare,
                    },
                    {
                      label: "Edit",
                      onClick: () => {
                        setIsEditModalOpen(true);
                        setMenuOpen(false);
                      },
                      icon: IconPencil,
                    },
                    {
                      label: "Add to Project",
                      onClick: () => {
                        setShowProjectModal(true);
                        setMenuOpen(false);
                      },
                      icon: VideoProductionIcon,
                    },
                    {
                      label: "Duplicate",
                      onClick: () => {
                        setDuplicateNameModalOpen(true);
                        setMenuOpen(false);
                      },
                      icon: DuplicateIcon,
                    },
                    {
                      label: "Delete Shortlist",
                      onClick: () => {
                        setIsDeleteModalOpen(true);
                        setMenuOpen(false);
                      },
                      icon: BinIcon,
                    },
                  ]}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                />
              </div>
            </div>

            <BrowsePros
              showDialog={showDialog}
              setShowDialog={setShowDialog}
              showShortlistFilter={false}
              isExpertPage={false}
              isShortlistPage
              addExpertToShortlist={() => setIsModalOpen(true)}
              queryParams={{ shortlistId: id }}
            />

            <AddShortlistModal
              open={isModalOpen}
              setOpen={() => {
                setIsModalOpen(false);
                refreshSearchParams();
              }}
              shortlist={shortlist}
              refresh={() => {}}
            />

            <ShortlistFormModal
              mode="edit"
              open={isEditModalOpen}
              setOpen={setIsEditModalOpen}
              shortlistId={shortlist?.id}
              currentName={shortlistName}
              updateShortlistName={updateShortlistName}
              fetchShortlists={() => {}}
            />

            <ConfirmationDialog
              open={showClearConfirmModal}
              onClose={() => setShowClearConfirmModal(false)}
              onConfirm={clearShortlist}
              title="Clear Shortlist"
              description="Are you sure you want to clear this shortlist?"
              disabled={isClearingShortlist}
            />

            <DeleteShortlistModal
              open={isDeleteModalOpen}
              setOpen={setIsDeleteModalOpen}
              shortlistId={shortlist?.id}
              fetchShortlists={() => {
                navigate("/experts/shortlists");
              }}
            />

            {showProjectModal && (
              <AddProjectToShortlist
                open={showProjectModal}
                setOpen={setShowProjectModal}
                shortlistId={shortlist.id}
                onSuccess={() => {}}
              />
            )}

            {duplicateNameModalOpen && (
              <ShortlistFormModal
                mode="duplicate"
                open={duplicateNameModalOpen}
                setOpen={setDuplicateNameModalOpen}
                shortlistId={shortlist?.id}
                currentName={shortlist?.name}
                onShortlistCreated={() => {}}
              />
            )}
          </div>
        )}
      </Box>
    </MainContainer>
  );
}
